import "@theme/styles.scss";
import GameScreen from "@screens/Game-Screen";
import HomeScreen from "@screens/Home-Screen";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { createContext } from "react";
import NotFoundScreen from "@screens/NotFound-Screen";
import AdminScreen from "@screens/Admin-Screen";
import { useCapturapedia, useCurrentUser, useGamesList } from "utils/firebase";
import { ScrollToTopOnMount } from "scripts/FunctionsBundle";
import LoginForm from "@components/LoginForm";
import GlobalNavbar from "@components/GlobalNavbar";
import MainScreen from "@screens/Main-Screen";
import GlobalFooter from "@components/GlobalFooter";
import ContactScreen from "@screens/Contact-Screen";
import AboutScreen from "@screens/About-Screen";
import ProjectScreen from "@screens/ProjectScreen";

// ############################ GLOBAL INTERFACES ############################

type _Capturapedia = import("utils/class-types").Capturapedia;
type _GameObject = import("utils/class-types").GameObject;

type Global = {
  user: {
    email: string;
    uid: string;
  };
  get: {
    Capturapedia: _Capturapedia;
    GamesList: Array<_GameObject>;
  };
  update: {
    Capturapedia: () => void;
    GamesList: () => void;
  };
};

// DEFAULTS
const defaultProps: Global = {
  user: {
    email: "",
    uid: "",
  },
  get: {
    Capturapedia: {},
    GamesList: [],
  },
  update: {
    Capturapedia: () => {},
    GamesList: () => {},
  },
};

// Provider
export const GlobalContext = createContext<Global>(defaultProps);

function App() {
  // Get Firestore Data and Hooks at first
  const currentUser = useCurrentUser();
  const [Capturapedia, updateCapturapedia] = useCapturapedia();
  const [GamesList, updateGamesList] = useGamesList();

  return (
    <>
      <GlobalContext.Provider
        value={{
          user: {
            email: currentUser?.email || "",
            uid: currentUser?.uid || "",
          },
          get: {
            Capturapedia: Capturapedia,
            GamesList: GamesList,
          },
          update: {
            Capturapedia: updateCapturapedia,
            GamesList: updateGamesList,
          },
        }}
      >
        <BrowserRouter>
          {/* GLOBAL NAV BAR */}
          <GlobalNavbar />

          {/* CHANGE ROUTES */}
          <Routes>
            <Route element={<ScrollToTopOnMount />} />

            <Route path="/" element={<MainScreen />}>
              <Route path="/home" />
            </Route>

            <Route path="/games">
              <Route index element={<HomeScreen />} />
              <Route path=":gameID" element={<GameScreen />} />
            </Route>

            <Route path="/admin">
              <Route
                index
                element={!currentUser ? <LoginForm /> : <AdminScreen />}
              />
            </Route>

            <Route path="/projects/:projectid" element={<ProjectScreen />} />

            <Route path="/about" element={<AboutScreen />} />

            <Route path="/suggestions" element={<ContactScreen />} />

            <Route path="/help" element={<NotFoundScreen />} />

            <Route
              path="*"
              element={<Navigate to={"/help"} replace={true} />}
            />
          </Routes>

          {/* GLOBAL FOOTER */}
          <GlobalFooter />
        </BrowserRouter>
      </GlobalContext.Provider>
    </>
  );
}

export default App;
